/**
 * ロジック図の置き場所 — 横は線の深さ、縦は段で決める。列の間にガター、段の下に水路の帯を取る。
 *
 * **横の列は線の深さである。** 出し手を持たない節点（入力）は受け手の列のすぐ左に置く — 入力は使う
 * 業務ルールの直前に揃える（使う箱のすぐ左で読めるように）。それ以外は、出し手の列の最大 ＋ 1 に置く。
 *
 * **縦は段で決める。** 段 0 が一番上で、段ごとに箱の高さの最大を積んで段の上端を決める。同じ列 ・ 同じ段に
 * 箱が 2 つ以上あれば、段の中で下へ重ねる。段の中では縦の中央に揃える。入力は、置く場所に流れの箱が
 * あれば 1 段下へ逃がす。
 *
 * **線の通り道を先に取る。** 線は業務フロー図の作図規約（`docs/web/spec-console-flow-drawing.md`）の
 * 規則 2〜6 で引く（`logic-edges.ts`）。そのために、列の右にガター（縦線の帯 ・ ラベルの帯 ・ 上がる帯）を、
 * 段の下に水路の帯を取る。寸法は規約のとおり業務フロー図とは共有せず、ここに持つ（値は同じ帯の考え方）。
 *
 * 材料の並びと値だけで決まるので、同じ材料からは毎回同じ置き場所になる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点を線の深さの列と段に置き、箱の位置と大きさを決める。
 * @graph-connects none
 */
import { LABEL_ZONE_WIDTH } from '../user-story-flow-layout.js';
import {
  atOr,
  getOr,
  type LogicDiagramModel,
  type LogicNode,
  type LogicNodeKind,
} from './logic-model.js';

/** 種類ごとの箱の幅。 @graph-connects none */
export const LOGIC_NODE_WIDTH: Readonly<Record<LogicNodeKind, number>> = {
  input: 220,
  rule: 240,
  ruleOutput: 180,
  branch: 280,
  effect: 240,
  end: 260,
};

/** ガターの縦線の帯の幅（線が縦に降りる）。 @graph-connects none */
export const LOGIC_GUTTER_LINE_ZONE = 72;
/** ガターの帯どうしの隙間。 @graph-connects none */
export const LOGIC_GUTTER_INSET = 8;
/** ガターの上がる帯の幅（終点の手前で線が縦に上がる）。 @graph-connects none */
export const LOGIC_GUTTER_RISE_ZONE = 64;
/** 段の中の箱の下端と水路の帯の間。 @graph-connects none */
export const LOGIC_CHANNEL_MARGIN = 24;
/** 水路の帯の最小の高さ。 @graph-connects none */
const CHANNEL_MIN_ZONE = 36;
/** 水路 1 本ぶんの刻み。 @graph-connects none */
export const LOGIC_CHANNEL_STEP = 6;
/** 水路の帯と次の段の間。 @graph-connects none */
const LANE_GAP = 24;
/** 同じ列 ・ 同じ段に重ねる箱の間。 @graph-connects none */
const STACK_GAP = 16;
/** 図の外周の余白。 @graph-connects none */
const MARGIN = 24;
/** 1 行の高さ。 @graph-connects none */
export const LOGIC_LINE_HEIGHT = 18;
/** 種類のタグの行の高さ。 @graph-connects none */
const TAG_HEIGHT = 22;
/** 箱の上下の余白。 @graph-connects none */
const BOX_PADDING = 16;
/** 分岐のひし形の最小の高さ。 @graph-connects none */
const BRANCH_MIN_HEIGHT = 120;

/**
 * 箱 1 つの位置と大きさ。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の箱 1 つの位置（左上）と大きさを表す。
 * @graph-connects none
 */
export interface LogicBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * 図 1 枚の置き場所。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図 1 枚の節点の箱・図全体の幅と高さを表す。
 * @graph-connects none
 */
export interface LogicLayout {
  /** 節点の鍵 → 箱 */
  readonly boxes: ReadonlyMap<string, LogicBox>;
  /** 節点の鍵 → 列 */
  readonly columnOf: ReadonlyMap<string, number>;
  /** 節点の鍵 → 段（入力を逃がした後） */
  readonly laneOf: ReadonlyMap<string, number>;
  /** 列ごとの升目の左端 */
  readonly columnLeft: readonly number[];
  /** 列ごとの升目の幅 */
  readonly columnWidths: readonly number[];
  /** 列ごとのガターの幅（升目の右端から次の升目の左端まで） */
  readonly gutterWidths: readonly number[];
  /** 段ごとの水路の帯（上端 ・ 下端） */
  readonly channels: readonly { readonly top: number; readonly bottom: number }[];
  readonly width: number;
  readonly height: number;
}

/**
 * ラベルの帯の幅。条件ラベルを描く部品（`FlowGutterEdge`）が業務フロー図と共通なので、ラベルの幅も
 * その部品に合わせる。 @graph-connects none
 */
export const LOGIC_LABEL_ZONE_WIDTH = LABEL_ZONE_WIDTH;

/** 条件ラベルが入る列のガターの幅（縦線 ・ 隙間 ・ ラベル ・ 上がる）。 @graph-connects none */
export const LOGIC_GUTTER_WITH_LABEL =
  LOGIC_GUTTER_LINE_ZONE + LOGIC_GUTTER_INSET + LOGIC_LABEL_ZONE_WIDTH + LOGIC_GUTTER_RISE_ZONE;
/** 条件ラベルが入らない列のガターの幅（縦線 ・ 上がる）。 @graph-connects none */
export const LOGIC_GUTTER_WITHOUT_LABEL = LOGIC_GUTTER_LINE_ZONE + LOGIC_GUTTER_RISE_ZONE;

/**
 * 名前を 1 行に何文字入れるか（全角の目安）。箱の幅と字の大きさから決める。
 * @graph-connects none
 */
function charsPerLine(width: number): number {
  return Math.max(4, Math.floor((width - 24) / 13));
}

/**
 * 文字列が箱の中で何行になるかの見積もり。半角は全角の 0.55 字として数える。
 * @graph-connects none
 */
function wrappedLines(text: string, width: number): number {
  if (text === '') return 0;
  let units = 0;
  for (const char of text) units += char.charCodeAt(0) < 0x80 ? 0.55 : 1;
  return Math.max(1, Math.ceil(units / charsPerLine(width)));
}

/**
 * 箱の高さの見積もり。タグの行 ＋ 名前の行 ＋ 中の行。分岐はひし形なので、内側の幅で折り返し、高さは倍を取る。
 *
 * @param node 節点
 * @returns 箱の高さ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の箱の高さを、タグ・名前・中の行の数から見積もる。
 * @graph-connects none
 */
export function logicNodeHeight(node: LogicNode): number {
  const width = LOGIC_NODE_WIDTH[node.kind];
  /* 分岐はひし形で、文字を置けるのは内側の中央（幅の半分）だけ。高さは中身の倍を取る。 */
  const textWidth = node.kind === 'branch' ? width / 2 : width;
  const titleLines = wrappedLines(node.title, textWidth);
  const bodyLines = node.lines.reduce((sum, line) => sum + wrappedLines(line, textWidth), 0);
  const content = BOX_PADDING + TAG_HEIGHT + (titleLines + bodyLines) * LOGIC_LINE_HEIGHT;
  return node.kind === 'branch' ? Math.max(BRANCH_MIN_HEIGHT, content * 2) : content;
}

/**
 * 節点ごとの列（線の深さ）を決める。
 *
 * 出し手を持たない節点は、受け手の列の最小 − 1 に置く（受け手が無ければ 0）。それ以外は出し手の列の
 * 最大 ＋ 1。環のある材料でも止まるよう、1 つの節点は並びの数より深くしない。
 *
 * @param model 材料
 * @returns 節点の鍵 → 列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点の列を、出し手の列の最大 ＋ 1（入力は受け手の直前）で決める。
 * @graph-connects none
 */
export function logicColumnsOf(model: LogicDiagramModel): Map<string, number> {
  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  for (const node of model.nodes) {
    incoming.set(node.key, []);
    outgoing.set(node.key, []);
  }
  for (const edge of model.edges) {
    incoming.get(edge.to)?.push(edge.from);
    outgoing.get(edge.from)?.push(edge.to);
  }
  const limit = model.nodes.length;
  const memo = new Map<string, number>();
  const depthOf = (key: string, guard: number): number => {
    const known = memo.get(key);
    if (known !== undefined) return known;
    const from = getOr(incoming, key, []);
    const depth =
      from.length === 0 || guard >= limit
        ? 0
        : Math.max(...from.map((source) => depthOf(source, guard + 1))) + 1;
    memo.set(key, depth);
    return depth;
  };
  const columns = new Map<string, number>();
  for (const node of model.nodes) columns.set(node.key, depthOf(node.key, 0));
  /* 入力は、使う節点のうち一番左の列のすぐ左へ寄せる。 */
  for (const node of model.nodes) {
    if (getOr(incoming, node.key, []).length > 0) continue;
    const targets = getOr(outgoing, node.key, []).map((target) => getOr(columns, target, 1));
    if (targets.length > 0) columns.set(node.key, Math.max(0, Math.min(...targets) - 1));
  }
  return columns;
}

/**
 * ロジック図の置き場所を決める。
 *
 * @param model 材料
 * @returns 箱の位置と大きさ、図全体の幅と高さ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点を列と段に置き、列の右にガター・段の下に水路の帯を取って、図全体の大きさと線の通り道の座標を決める。
 * @graph-connects logicColumnsOf [calls] 節点の列
 * @graph-connects logicNodeHeight [calls] 箱の高さの見積もり
 */
export function computeLogicLayout(model: LogicDiagramModel): LogicLayout {
  const columns = logicColumnsOf(model);
  const columnCount = Math.max(0, ...columns.values()) + 1;

  /* 列の幅は、その列の箱の幅の最大。ガターは、その列から出る線に条件のラベルがあれば帯を 1 本足す。 */
  const columnWidths = Array.from({ length: columnCount }, () => 0);
  for (const node of model.nodes) {
    const column = getOr(columns, node.key, 0);
    columnWidths[column] = Math.max(atOr(columnWidths, column, 0), LOGIC_NODE_WIDTH[node.kind]);
  }
  const labeled = new Set(
    model.edges
      .filter((edge) => edge.label !== undefined)
      .map((edge) => getOr(columns, edge.from, 0)),
  );
  const gutterWidths = columnWidths.map((_width, column) =>
    labeled.has(column) ? LOGIC_GUTTER_WITH_LABEL : LOGIC_GUTTER_WITHOUT_LABEL,
  );
  const columnLeft: number[] = [];
  let x = MARGIN;
  for (const [column, width] of columnWidths.entries()) {
    columnLeft.push(x);
    x += width + atOr(gutterWidths, column, LOGIC_GUTTER_WITHOUT_LABEL);
  }

  /*
   * 入力は、置く場所（列 ・ 段）に入力でない箱があれば 1 段下へ逃がす。同じ場所に積むと、段の中央揃えで
   * 流れの箱が段からずれ、矢印が折れる。
   */
  const occupied = new Set(
    model.nodes
      .filter((node) => node.kind !== 'input')
      .map((node) => `${String(getOr(columns, node.key, 0))}:${String(node.lane)}`),
  );
  const laneOf = new Map<string, number>();
  for (const node of model.nodes) {
    let lane = node.lane;
    if (node.kind === 'input') {
      const column = String(getOr(columns, node.key, 0));
      while (occupied.has(`${column}:${String(lane)}`)) lane += 1;
    }
    laneOf.set(node.key, lane);
  }

  /* 段の高さは、段の中で同じ列に重なる箱の高さの合計の最大。 */
  const cells = new Map<string, { lane: number; nodes: LogicNode[] }>();
  for (const node of model.nodes) {
    const lane = getOr(laneOf, node.key, node.lane);
    const cell = `${String(getOr(columns, node.key, 0))}:${String(lane)}`;
    const stack = getOr(cells, cell, { lane, nodes: [] });
    stack.nodes.push(node);
    cells.set(cell, stack);
  }
  const laneCount = Math.max(0, ...laneOf.values()) + 1;
  const laneHeights = Array.from({ length: laneCount }, () => 0);
  for (const { lane, nodes } of cells.values()) {
    const total =
      nodes.reduce((sum, node) => sum + logicNodeHeight(node), 0) + (nodes.length - 1) * STACK_GAP;
    laneHeights[lane] = Math.max(atOr(laneHeights, lane, 0), total);
  }
  /* 水路の帯の高さは、その段から出る線の本数ぶんの刻み（最小を下回らない）。 */
  const outgoingByLane = Array.from({ length: laneCount }, () => 0);
  for (const edge of model.edges) {
    const lane = laneOf.get(edge.from);
    if (lane !== undefined) outgoingByLane[lane] = atOr(outgoingByLane, lane, 0) + 1;
  }
  const laneTop: number[] = [];
  const channels: { top: number; bottom: number }[] = [];
  let y = MARGIN;
  for (const [lane, height] of laneHeights.entries()) {
    laneTop.push(y);
    const zone = Math.max(CHANNEL_MIN_ZONE, atOr(outgoingByLane, lane, 0) * LOGIC_CHANNEL_STEP);
    const top = y + height + LOGIC_CHANNEL_MARGIN;
    channels.push({ top, bottom: top + zone });
    /* 箱の無い段は間を取らない。 */
    y += height > 0 ? height + LOGIC_CHANNEL_MARGIN + zone + LANE_GAP : 0;
  }

  const boxes = new Map<string, LogicBox>();
  for (const { lane, nodes } of cells.values()) {
    /* 段の中では縦の中央に揃える。高さの違う箱（分岐のひし形）の間でも矢印がまっすぐになる。 */
    const stackHeight =
      nodes.reduce((sum, node) => sum + logicNodeHeight(node), 0) + (nodes.length - 1) * STACK_GAP;
    let top =
      atOr(laneTop, lane, MARGIN) + (atOr(laneHeights, lane, stackHeight) - stackHeight) / 2;
    for (const node of nodes) {
      const column = getOr(columns, node.key, 0);
      const w = LOGIC_NODE_WIDTH[node.kind];
      const h = logicNodeHeight(node);
      /* 列の幅より細い箱は列の中央に置き、列の中で線の出入りの高さを揃える。 */
      boxes.set(node.key, {
        x: atOr(columnLeft, column, MARGIN) + (atOr(columnWidths, column, w) - w) / 2,
        y: top,
        w,
        h,
      });
      top += h + STACK_GAP;
    }
  }

  const right = Math.max(MARGIN, ...[...boxes.values()].map((box) => box.x + box.w));
  const bottom = Math.max(MARGIN, y - LANE_GAP, ...[...boxes.values()].map((box) => box.y + box.h));
  return {
    boxes,
    columnOf: columns,
    laneOf,
    columnLeft,
    columnWidths,
    gutterWidths,
    channels,
    width: right + MARGIN,
    height: bottom + MARGIN,
  };
}
